import { Tooltip } from '@material-ui/core';
import React, { useState } from 'react';

import { IDeliveryVariables } from '~/src/dash/apiV2/forecasting/forecasting';
import { SimpleSwitch } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/SimpleSwitch';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { highlightedDivStyle } from '~/src/dash/components/layout/common';
import { Col, Cols, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { AdjustmentToTraffic } from '~/src/dash/views/tools/Forecasting/DeliveryVariables/AdjustmentToTraffic';
import { FrequencyCapping } from '~/src/dash/views/tools/Forecasting/DeliveryVariables/FrequencyCapping';
import { TimeRangeAdjustment } from '~/src/dash/views/tools/Forecasting/DeliveryVariables/TimeRangeAdjustment';

export interface IDeliveryVariablesParams {
  deliveryVariables: IDeliveryVariables;
  setDeliveryVariables: React.Dispatch<React.SetStateAction<IDeliveryVariables>>;
}

// Exclude House Campaigns Sub Component
const ExcludeHouseCampaigns = ({
  deliveryVariables,
  setDeliveryVariables,
}: IDeliveryVariablesParams): JSX.Element => {
  const [isEnabled, setIsEnabled] = useState(deliveryVariables.ExcludeHouseCampaigns);

  const handleToggleExcludeHouseCampaigns = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setIsEnabled(e.target.checked);
    // clone necessary to trigger rerender on deliveryVariables for Forecast component
    const newDeliveryVariables = { ...deliveryVariables } as IDeliveryVariables;
    newDeliveryVariables.ExcludeHouseCampaigns = e.target.checked;
    setDeliveryVariables(newDeliveryVariables);
  };

  return (
    <Rows gap={spacing.base}>
      <Row noPadding>
        <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
          <Col>
            <Text bold small>
              {isEnabled ? 'Active' : 'Inactive'}
            </Text>
          </Col>
          <Col>
            <SimpleSwitch
              isChecked={isEnabled}
              handleSwitch={handleToggleExcludeHouseCampaigns}
              key='FrequencyCapping'
              inputID='frequency_capping_enabled_toggle'
            />
          </Col>
          <Col>
            <Tooltip
              title='This variable excludes direct campaigns with type `House` from the forecast.'
              placement='top'
            >
              <div style={{ display: 'flex' }}>
                <IconV2 large name='info-static' color={publicaColors.gray} />
              </div>
            </Tooltip>
          </Col>
        </Cols>
      </Row>
    </Rows>
  );
};

// Delivery Variables Main Componenet
export const DeliveryVariables = ({
  deliveryVariables,
  setDeliveryVariables,
}: IDeliveryVariablesParams): JSX.Element => {
  return (
    <Rows smallPadding style={{ ...highlightedDivStyle, minWidth: '940px' }}>
      <Row smallPadding>
        <Text bold large color={publicaColors.black}>
          Delivery Variables
        </Text>
      </Row>
      <Row smallVerticalPadding>
        <Cols gap={spacing.huge}>
          <Col
            style={{
              minWidth: '180px',
            }}
          >
            <Text small bold>
              Time Range Adjustment
            </Text>
          </Col>
          <Col>
            <TimeRangeAdjustment deliveryVariables={deliveryVariables} />
          </Col>
        </Cols>
      </Row>
      <Row smallVerticalPadding>
        <Cols gap={spacing.huge}>
          <Col
            style={{
              minWidth: '180px',
            }}
          >
            <Text small bold>
              Frequency Capping
            </Text>
          </Col>
          <Col>
            <FrequencyCapping
              deliveryVariables={deliveryVariables}
              setDeliveryVariables={setDeliveryVariables}
            />
          </Col>
        </Cols>
      </Row>
      <Row smallVerticalPadding>
        <Cols gap={spacing.huge}>
          <Col
            style={{
              minWidth: '180px',
            }}
          >
            <Text small bold>
              Exclude House Campaigns
            </Text>
          </Col>
          <Col>
            <ExcludeHouseCampaigns
              deliveryVariables={deliveryVariables}
              setDeliveryVariables={setDeliveryVariables}
            />
          </Col>
        </Cols>
      </Row>
      <Row smallVerticalPadding>
        <Cols gap={spacing.huge}>
          <Col
            style={{
              minWidth: '180px',
            }}
          >
            <Text small bold>
              Adjustment to Traffic
            </Text>
          </Col>
          <Col>
            <AdjustmentToTraffic deliveryVariables={deliveryVariables} />
          </Col>
        </Cols>
      </Row>
    </Rows>
  );
};
